import { Dispatch, useReducer } from 'react';

type State<T> = {
  /**
   * Are we currently loading new data?
   */
  loading: boolean;
  /**
   * Possible data, null if no successful call has been made
   */
  data: T | null;
  /**
   * Error returned from the hook
   */
  error: Error | null;
  /**
   * Have we called the endpoint yet?
   */
  called: boolean;
  /**
   * Number of times the request has been made
   */
  callCount: number;
  /**
   * Number representation of the date object the call was last made, if at all.
   */
  lastCalled: number | null;
};

type Request = { type: 'REQUEST' };

type Success<T> = {
  type: 'SUCCESS';
  payload: T;
};

type Failure = {
  type: 'FAILURE';
  payload: Error;
};

type Action<T> = Request | Success<T> | Failure;

function reducer<T>(state: State<T>, action: Action<T>): State<T> {
  switch (action.type) {
    case 'REQUEST':
      return {
        ...state,
        loading: true,
        called: true,
        callCount: state.callCount + 1,
        lastCalled: Date.now(),
      };
    case 'SUCCESS':
      return { ...state, loading: false, error: null, data: action.payload };
    case 'FAILURE':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

export function useInternalState<T>(initialData?: T): [State<T>, Dispatch<Action<T>>] {
  return useReducer(reducer, {
    loading: false,
    error: null,
    data: initialData ?? null,
    called: false,
    callCount: 0,
    lastCalled: null,
  });
}

type HasQuery = {
  query: Record<string, unknown>;
};

function hasQuery(x: Record<string, unknown>): x is HasQuery {
  return typeof x.query === 'object' && !Array.isArray(x.query);
}

type HasSelect = {
  select: string[];
};

function hasSelect(x: Record<string, unknown>): x is HasSelect {
  return Array.isArray(x.select);
}

export function serializeQuery(options: Record<string, unknown>): string {
  let res: string[] = [];

  if (hasQuery(options)) {
    res.push(
      Object.entries(options?.query ?? {})
        .filter(([, value]) => typeof value !== 'undefined')
        .sort(([a], [b]) => (a < b ? 1 : -1))
        .map(([k, v]) => `${k}=${v}`)
        .join('&'),
    );
  }

  if (hasSelect(options)) {
    res.push('fields=' + options.select.slice().sort().join(','));
  }

  if (res.length > 0) {
    return '?' + res.join('&');
  }

  return '';
}

type MockData<T> = {
  data?: T;
};

type SwrHook<T> = (getOptions: any, config?: any) => MockData<T>;
type CallbackHook<T> = () => readonly [(options: any) => Promise<void>, MockData<T>];

type GetData<T> = T extends SwrHook<infer D1> ? D1 : T extends CallbackHook<infer D2> ? D2 : never;

interface MswTarget {
  (...args: any[]): any;
}

interface MswData<T extends MswTarget = () => any> {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS';
  endpoint: string;
}

let msw = new WeakMap<MswTarget, MswData>();

export function assignMswData<T extends MswTarget>(target: T, data: MswData<T>) {
  msw.set(target, data);
}

export function getMswData<T extends MswTarget>(target: T): MswData<T> {
  let data = msw.get(target);

  if (!data) {
    throw new Error('mswjs data not available on target');
  }

  return data;
}
